import { NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { InboxService } from '@core/inbox/inbox.service';
import { LeadService } from '@core/lead/lead.service';
import { RequestService } from '@core/request/request.service';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { filter, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'classy-layout',
  templateUrl: './classy.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    NotificationsComponent,
    UserComponent,
    MatIconModule,
    MatButtonModule,
    LanguagesComponent,
    FuseFullscreenComponent,
    SearchComponent,
    ShortcutsComponent,
    MessagesComponent,
    RouterOutlet,
    QuickChatComponent,
    NgIf,
    RouterLink
  ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;
  navigation: Navigation;
  user: User;
  navigationAppearance: 'default' | 'dense' = 'default';
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  inboxService = inject(InboxService);
  leadService = inject(LeadService);
  requestService = inject(RequestService);

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _navigationService: NavigationService,
    private _userService: UserService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService
  ) { }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
        this.updateBadge();
      });

    // Subscribe to the user service
    this._userService.user$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((user: User) => {
        this.user = user;
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');

        // Change the navigation appearance
        this.navigationAppearance = this.isScreenSmall
          ? 'dense'
          : 'default';
      });


    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe((e: any) => {
        this.updateBadge();
      });

    this.inboxService.refresh$.subscribe(() => {
      this.updateBadge();
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  /**
* Toggle the navigation appearance
*/
  toggleNavigationAppearance(): void {
    this.navigationAppearance =
      this.navigationAppearance === 'default' ? 'dense' : 'default';
  }

  updateBadge(): void {

    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        'mainNavigation'
      );

    if (this.navigation.items.length < 1) {
      return;
    }

    this.inboxService.getTotalUnreadMails().subscribe((inbox) => {
      const navigation =
        this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
          'mainNavigation'
        );
      this.navigation.items = this.navigation.items.map((item) => {
        if (item.internalId === 'inbox' && inbox > 0) {
          item.badge = {
            title: inbox.toString(),
            classes: 'px-2 bg-pink-600 text-white rounded-full',
          }
        }
        return item;
      });
      navigation.refresh();
    });

    this.leadService.getTotalLeadsByStatusNew().subscribe((leads) => {
      const navigation =
        this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
          'mainNavigation'
        );
      this.navigation.items = this.navigation.items.map((item) => {
        if (item.internalId === 'leads' && leads > 0) {
          item.badge = {
            title: leads.toString(),
            classes: 'px-2 bg-pink-600 text-white rounded-full',
          }
        }
        return item;
      });
      navigation.refresh();
    }
    );
    this.requestService.getTotalRequestsByStatusOpen().subscribe((requests) => {
      const navigation =
        this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
          'mainNavigation'
        );
      this.navigation.items = this.navigation.items.map((item) => {
        if (item.internalId === 'requests' && requests > 0) {
          item.badge = {
            title: requests.toString(),
            classes: 'px-2 bg-pink-600 text-white rounded-full',
          }
        }
        return item;
      });
      navigation.refresh();
    });

  }
}
